import React, { useState, useEffect } from 'react';
import ReactApexCharts from 'react-apexcharts';
import moment from 'moment';
import { Box, Typography, Paper, CircularProgress } from '@mui/material';
import axios from 'axios';
import { useSelector } from 'react-redux';

const DockUtilizationVerticalChart = () => {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [minTime, setMinTime] = useState(moment().startOf('day').valueOf());
  const [maxTime, setMaxTime] = useState(moment().endOf('day').valueOf());
  const currentDate = useSelector((state) => state.customization.currentDate);

  useEffect(() => {
    fetchDockData();
  }, [currentDate]);

  const fetchDockData = async () => {
    try {
      setLoading(true);
      setError(null);

      const requestBody = {
        startDate: {
          date: currentDate
        },
        date: currentDate
      };

      const response = await axios.post('https://neoe2e.neophyte.live/fm-api/v2/truck_info', requestBody);

      if (response.data && response.data.length > 0) {
        processTruckTimelineData(response.data);
      } else {
        setSeries([]);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching truck data:', error);
      setError('Failed to load truck timeline data. Please try again later.');
      setSeries([]);
      setLoading(false);
    }
  };

  const formatTime = (minutes) => {
    if (isNaN(minutes) || minutes <= 0) return '0 Mins';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours} Hrs ${mins} Mins` : `${mins} Mins`;
  };

  // const processTruckTimelineData = (data) => {
  //   try {
  //     if (!data || data.length === 0 || !data[0].trucks) {
  //       setSeries([]);
  //       setLoading(false);
  //       return;
  //     }

  //     const trucks = data[0].trucks;

  //     const timelineSeries = trucks.map((truck, index) => {
  //       const startTime = moment(truck.start_time).local();
  //       const endTime = moment(truck.end_time).local();

  //       return {
  //         name: `Truck ${index + 1}`,
  //         data: [
  //           {
  //             x: 'Dock',
  //             y: [startTime.valueOf(), endTime.valueOf()]
  //           }
  //         ],
  //         startTimeFormatted: startTime.format('HH:mm'),
  //         endTimeFormatted: endTime.format('HH:mm'),
  //         idleTime: formatTime(truck.idle_time || 0),
  //         unloadingTime: formatTime(truck.unloading_tat || 0)
  //       };
  //     });

  //     setSeries(timelineSeries);
  //     setLoading(false);
  //   } catch (error) {
  //     console.error('Error processing truck timeline data:', error);
  //     setError('Error processing truck timeline data.');
  //     setSeries([]);
  //     setLoading(false);
  //   }
  // };

  const processTruckTimelineData = (data) => {
    try {
      if (!data || data.length === 0 || !data[0].trucks) {
        setSeries([]);
        setLoading(false);
        return;
      }

      const trucks = data[0].trucks;
      let minTime = moment(currentDate).startOf('day'); // Start of current day (00:00)
      let maxTime = moment(currentDate).endOf('day'); // End of current day (23:59)

      const timelineSeries = trucks.map((truck, index) => {
        const startTime = moment(truck.start_time).local();
        const endTime = moment(truck.end_time).local();

        // Update minTime if this truck starts earlier
        if (startTime.isBefore(minTime)) {
          minTime = moment(startTime).startOf('day');
        }

        // Update maxTime if this truck ends later
        if (endTime.isAfter(maxTime)) {
          maxTime = moment(endTime).endOf('day');
        }

        return {
          name: `Truck ${index + 1}`,
          data: [
            {
              x: 'Dock',
              y: [startTime.valueOf(), endTime.valueOf()]
            }
          ],
          startTimeFormatted: startTime.format('HH:mm'),
          endTimeFormatted: endTime.format('HH:mm'),
          idleTime: formatTime(truck.idle_time || 0),
          unloadingTime: formatTime(truck.unloading_tat || 0)
        };
      });

      // Store the min/max times in state to use in chart options
      setSeries(timelineSeries);
      setMinTime(minTime.valueOf());
      setMaxTime(maxTime.valueOf());
      setLoading(false);
    } catch (error) {
      console.error('Error processing truck timeline data:', error);
      setError('Error processing truck timeline data.');
      setSeries([]);
      setLoading(false);
    }
  };

  const getOptions = () => {
    if (!series.length) return {};

    return {
      chart: {
        height: 350,
        type: 'rangeBar',
        toolbar: {
          show: false
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800
        }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '70%',
          rangeBarGroupRows: true,
          colors: {
            ranges: [
              { from: 0, to: 1, color: '#007BFF' },
              { from: 2, to: 5, color: '#28a745' },
              { from: 6, to: 10, color: '#FFC107' }
            ]
          }
        }
      },
      // xaxis: {
      //   type: 'datetime',
      //   labels: {
      //     format: 'HH:mm',
      //     datetimeUTC: false,
      //     style: { fontSize: '12px', fontWeight: 600, colors: '#37474F' }
      //   },
      //   title: {
      //     text: 'Time of Day',
      //     style: { fontSize: '14px', fontWeight: 600, color: '#263238' }
      //   }
      // },

      xaxis: {
        type: 'datetime',
        min: minTime,
        max: maxTime,
        labels: {
          format: 'HH:mm',
          datetimeUTC: false,
          style: { fontSize: '12px', fontWeight: 600, colors: '#37474F' }
        },
        title: {
          text: 'Time of Day',
          style: { fontSize: '14px', fontWeight: 600, color: '#263238' }
        }
      },

      yaxis: {
        labels: {
          style: {
            fontSize: '14px',
            fontWeight: 600,
            color: '#37474F'
          }
        }
      },
      stroke: {
        width: 2,
        colors: ['#FFFFFF']
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'dark',
          type: 'vertical',
          gradientToColors: ['#0066FF', '#4CAF50'],
          stops: [0, 100]
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left',
        fontSize: '14px',
        fontWeight: 500
      },
      // tooltip: {
      //   theme: 'dark',
      //   custom: function (opts) {
      //     const truck = series[opts.seriesIndex];
      //     return `
      //       <div style="padding: 10px; border-radius: 8px; background: #222; color: #fff;">
      //         <div><b>${truck.name}</b></div>
      //         <div>Start: ${truck.startTimeFormatted}</div>
      //         <div>End: ${truck.endTimeFormatted}</div>
      //         <div>Idle Time: ${truck.idleTime}</div>
      //         <div>Unloading Time: ${truck.unloadingTime}</div>
      //       </div>
      //     `;
      //   }
      // },

      tooltip: {
        theme: 'dark',
        custom: function (opts) {
          const truck = series[opts.seriesIndex];
          const startTime = moment(truck.data[0].y[0]);
          const endTime = moment(truck.data[0].y[1]);

          // Show date only if it spans multiple days
          const startFormat = startTime.date() !== endTime.date() ? 'MMM DD, HH:mm' : 'HH:mm';
          const endFormat = startTime.date() !== endTime.date() ? 'MMM DD, HH:mm' : 'HH:mm';

          return `
            <div style="padding: 10px; border-radius: 8px; background: #222; color: #fff;">
              <div><b>${truck.name}</b></div>
              <div>Start: ${startTime.format(startFormat)}</div>
              <div>End: ${endTime.format(endFormat)}</div>
              <div>Idle Time: ${truck.idleTime}</div>
              <div>Unloading Time: ${truck.unloadingTime}</div>
            </div>
          `;
        }
      },
      title: {
        text: 'Truck Dock Utilization Timeline',
        align: 'center',
        style: { fontSize: '18px', fontWeight: 600, color: '#263238' }
      },
      subtitle: {
        text: moment(currentDate).format('MMM DD, YYYY'),
        align: 'center',
        style: { fontSize: '14px', color: '#607d8b' }
      },
      dataLabels: {
        enabled: true,
        formatter: function (val) {
          const duration = moment
            .duration(moment(val[1]).diff(moment(val[0])))
            .asMinutes()
            .toFixed(0);
          return `${formatTime(duration)}`;
        },
        style: {
          fontSize: '12px',
          colors: ['#fff']
        },
        background: {
          enabled: true,
          foreColor: '#222',
          borderRadius: 3,
          padding: 4
        }
      }
    };
  };

  return (
    <Paper
      elevation={3}
      sx={{
        width: '100%',
        height: '450px',
        borderRadius: 2,
        overflow: 'hidden',
        background: 'linear-gradient(to right, #fafafa, #ffffff)',
        p: 2
      }}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress size={40} thickness={4} color="primary" />
          <Typography variant="body1" color="textSecondary" sx={{ ml: 2 }}>
            Loading truck timeline data...
          </Typography>
        </Box>
      ) : error ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', height: '100%' }}>
          <Typography variant="h6" color="error" align="center">
            Error
          </Typography>
          <Typography variant="body2" color="error" align="center">
            {error}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <button
              onClick={fetchDockData}
              style={{
                padding: '8px 16px',
                backgroundColor: '#0066FF',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </Box>
        </Box>
      ) : series.length > 0 ? (
        <ReactApexCharts options={getOptions()} series={series} type="rangeBar" height={400} />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', height: '100%' }}>
          <Typography variant="h6" color="textSecondary" align="center">
            No data available
          </Typography>
          <Typography variant="body2" color="textSecondary" align="center">
            No truck activity for the selected date.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default DockUtilizationVerticalChart;
